import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import {
  AuthFactoryProvider,
  LoadingOverlay,
  useAuth,
} from '@spotify/confidence-core-react';
import { FeatureFlagged } from '@spotify/confidence-flag-evaluation-react';
import {
  TrackingScope,
  withPageImpression,
} from '@spotify/plugin-confidence-tracking-react';

import { AccountCreated } from './AccountCreated';
import { CreateAccountForm } from './CreateAccountForm';
import { CreateAccountFormV2 } from './CreateAccountFormV2';
import { InviteError } from './InviteError';
import { CreatedAccount, useInvite, useRegionCheck } from './hooks';

const CLIENT_ID = '82qMvwZvqd3t3S0gRDvs8R53TehQXSJY';

const SignUpJourney = ({ inviteId }: { inviteId?: string }) => {
  const [token, setToken] = useState<string>();
  const [createdAccount, setCreatedAccount] = useState<CreatedAccount>();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: appAuthLoading } = useAuth();
  const [{ error: inviteError, loading, value: inviteData }, getAccountInvite] =
    useInvite(inviteId);
  const account = createdAccount ?? inviteData?.createdAccount;

  const {
    loading: regionLoading,
    error: regionError,
    regionData,
  } = useRegionCheck();

  useEffect(() => {
    const abortController = new AbortController();
    if (inviteId) {
      void getAccountInvite(abortController.signal);
    }
    return () => abortController.abort();
  }, [getAccountInvite, inviteId]);

  useEffect(() => {
    if (regionError) {
      // eslint-disable-next-line no-console
      console.warn(
        'Region check failed, allowing signup to proceed:',
        regionError,
      );
      return;
    }

    if (!regionLoading && regionData?.allowed === false) {
      navigate('/signup/unsupported-market');
    }
  }, [regionLoading, regionError, regionData, navigate]);

  if (!inviteId && appAuthLoading) {
    return <LoadingOverlay loading />;
  }

  if (!inviteId && isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const renderContent = () => {
    if (loading || regionLoading) {
      return null;
    }
    if (inviteError) {
      return <InviteError error={inviteError} />;
    }
    if (account) {
      return <AccountCreated createdAccount={account} />;
    }
    return (
      <AuthFactoryProvider
        clientId={CLIENT_ID}
        onTokenReceived={t => setToken(t)}
      >
        <TrackingScope elementId="main.signup">
          <FeatureFlagged name="slim-signup-wizard">
            <FeatureFlagged.On>
              <CreateAccountFormV2
                inviteId={inviteId}
                token={token}
                onCreate={setCreatedAccount}
              />
            </FeatureFlagged.On>
            <FeatureFlagged.Off>
              <CreateAccountForm
                inviteId={inviteId}
                token={token}
                onCreate={setCreatedAccount}
                skipCompanyWizard={inviteData?.warehouseNative ?? false}
              />
            </FeatureFlagged.Off>
          </FeatureFlagged>
        </TrackingScope>
      </AuthFactoryProvider>
    );
  };

  return (
    <>
      <LoadingOverlay loading={loading || regionLoading} />
      {renderContent()}
    </>
  );
};

const SignUpPageComponent = () => {
  const { id: inviteId } = useParams();
  return <SignUpJourney key={inviteId ?? 'regular'} inviteId={inviteId} />;
};

export const SignUpPage = withPageImpression(SignUpPageComponent, {
  pageId: 'signup',
});
